import React from 'react';
import { Segment, Header, Icon, Label, Message } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { UserComment } from '../../app/models/UserRatingModels';

interface UserCommentsTabProps {
    comments: UserComment[];
    isOwner: boolean;
    isMobile: boolean;
    totalCount?: number;
    visibleCount?: number;
}

export default observer(function UserCommentsTab({ 
    comments, 
    isOwner,
    isMobile 
}: UserCommentsTabProps) {
    const { t } = useTranslation();
    
    // Filter comments based on user type
    const visibleComments = isOwner 
        ? comments // Owner sees all
        : comments.filter(c => c.isApproved); // Others only see approved

    if (visibleComments.length === 0) {
        return (
            <Message info>
                <Message.Header>
                    {isOwner 
                        ? t('userComments.noComments.ownerTitle')
                        : t('userComments.noComments.visitorTitle')
                    }
                </Message.Header>
                <p>
                    {isOwner 
                        ? t('userComments.noComments.ownerDescription')
                        : t('userComments.noComments.visitorDescription')
                    }
                </p>
            </Message>
        );
    }

    return (
        <div style={{ padding: isMobile ? '5px' : '10px' }}>
            <Message info>
                <Message.Header>{t('userComments.title')}</Message.Header>
                <p>
                    {t('userComments.description')}
                    {isOwner && (
                        <strong> {t('userComments.descriptionOwner').split('. ').pop()}</strong>
                    )}
                </p>
            </Message>

            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: isMobile ? '10px' : '15px' 
            }}>
                {visibleComments
                    .slice() // Create copy for sorting
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map(comment => (
                        <Segment 
                            key={comment.id}
                            style={{
                                border: comment.isApproved 
                                    ? '1px solid #21ba45' 
                                    : '1px solid #db2828',
                                opacity: comment.isApproved ? 1 : 0.7
                            }}
                        >
                            <div>
                                <Header as='h5' style={{ marginBottom: '5px' }}>
                                    <Icon name='user secret' />
                                    {t('userComments.anonymousUser')}
                                    {!comment.isApproved && (
                                        <Label 
                                            size='mini' 
                                            color='red'
                                            style={{ marginLeft: '10px' }}
                                        >
                                            {t('userComments.hiddenLabel')}
                                        </Label>
                                    )}
                                    {comment.isApproved && isOwner && (
                                        <Label 
                                            size='mini' 
                                            color='green'
                                            style={{ marginLeft: '10px' }}
                                        >
                                            {t('userComments.approvedLabel')}
                                        </Label>
                                    )}
                                </Header>
                                <p style={{ 
                                    fontSize: '0.85rem', 
                                    color: '#999', 
                                    marginBottom: '10px' 
                                }}>
                                    {new Date(comment.createdAt).toLocaleDateString()}
                                </p>
                                <p style={{ 
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                    lineHeight: '1.5',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {comment.comment}
                                </p>
                            </div>
                        </Segment>
                    ))}
            </div>
        </div>
    );
});